import { API_BASE_URL } from '../config';
import React, { useEffect, useState } from 'react';

const ACCEPTED = ['image/png', 'image/jpeg', 'image/webp'];

function LogoSlot({ kind, title, help, savedMessage, removeMessage, current }) {
  const [logo, setLogo] = useState(current || null);
  const [pending, setPending] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLogo(current || null);
  }, [current]);

  const onFile = (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;
    if (!ACCEPTED.includes(file.type)) {
      setError('Choose a PNG, JPEG, or WebP image.');
      return;
    }
    if (file.size > 1024 * 1024) {
      setError('Logo must be smaller than 1 MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setPending(reader.result);
      setError('');
      setMessage('');
    };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!pending) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/clinic/logo`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ kind, logo: pending })
      });
      const data = await response.json();
      if (!response.ok) {
        setError(data.message || 'Could not save the logo.');
        return;
      }
      const saved = kind === 'header' ? data.headerLogo : data.prescriptionLogo;
      setLogo(saved || pending);
      setPending(null);
      setMessage(savedMessage);
      if (kind === 'header') {
        window.dispatchEvent(new CustomEvent('clinic-header-logo', { detail: saved || pending }));
      }
    } catch (err) {
      setError('Could not save the logo.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(removeMessage)) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/clinic/logo?kind=${kind}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (!response.ok) {
        setError(data.message || 'Could not remove the logo.');
        return;
      }
      setLogo(null);
      setPending(null);
      setMessage('Logo removed.');
      if (kind === 'header') {
        window.dispatchEvent(new CustomEvent('clinic-header-logo', { detail: null }));
      }
    } catch (err) {
      setError('Could not remove the logo.');
    } finally {
      setSaving(false);
    }
  };

  const preview = pending || logo;

  return (
    <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#fff' }}>
      <h4 style={{ marginTop: 0 }}>{title}</h4>
      <p style={{ color: '#495057' }}>{help}</p>
      {preview ? (
        <img
          src={preview}
          alt={title}
          style={{ maxWidth: '100%', maxHeight: '120px', border: '1px solid #ddd', borderRadius: '4px', background: '#fff' }}
        />
      ) : (
        <p style={{ color: '#6c757d' }}>No logo yet.</p>
      )}
      <div style={{ marginTop: '1rem' }}>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
          {logo ? 'Replace logo' : 'Choose logo'}
        </label>
        <input type="file" accept="image/png,image/jpeg,image/webp" onChange={onFile} />
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={save}
          disabled={!pending || saving}
          style={{ padding: '10px 16px', backgroundColor: '#198754', color: 'white', border: 'none', borderRadius: '4px', cursor: pending && !saving ? 'pointer' : 'not-allowed' }}
        >
          {saving ? 'Saving...' : 'Save logo'}
        </button>
        {logo && (
          <button
            type="button"
            onClick={remove}
            disabled={saving}
            style={{ padding: '10px 16px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            Remove logo
          </button>
        )}
      </div>
      {message && <p style={{ color: '#198754' }}>{message}</p>}
      {error && <p style={{ color: '#dc3545' }}>{error}</p>}
    </div>
  );
}

const ClinicLogoSection = () => {
  const [logos, setLogos] = useState({ prescriptionLogo: null, headerLogo: null });
  const [error, setError] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${API_BASE_URL}/clinic/logo`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(async (response) => {
      if (!response.ok) {
        setError('Could not load the clinic logos.');
        return;
      }
      const data = await response.json();
      setLogos({
        prescriptionLogo: data.prescriptionLogo || data.logo || null,
        headerLogo: data.headerLogo || null
      });
    }).catch(() => setError('Could not load the clinic logos.'));
  }, []);

  return (
    <div>
      {error && <p style={{ color: '#dc3545' }}>{error}</p>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
        <LogoSlot
          kind="prescription"
          title="Prescription logo"
          help="Printed at the top of every prescription."
          savedMessage="Saved. This logo prints on every prescription."
          removeMessage="Remove the logo from future prescriptions?"
          current={logos.prescriptionLogo}
        />
        <LogoSlot
          kind="header"
          title="Header logo"
          help="Shown at the top left of the clinic screens."
          savedMessage="Saved. This logo shows at the top left."
          removeMessage="Remove the logo from the top left?"
          current={logos.headerLogo}
        />
      </div>
    </div>
  );
};

export default ClinicLogoSection;
